import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Shield, Heart, Car, Bike, RefreshCw, Loader2, Calendar } from 'lucide-react';

const LeadEnquiryPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Complete Healthcare');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [consent, setConsent] = useState(false);
  const [termsAgree, setTermsAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    gender: '',
    mobile_number: '',
    email: '',
    dob: '',
    id_type: '',
    id_number: '',
    insured_person: ''
  });

  const categories = [
    { id: 'complete-healthcare', name: 'Complete Healthcare', icon: Shield },
    { id: 'aplus-health', name: 'A Plus Health Insurance', icon: Heart },
    { id: 'motor-car', name: 'Motor Private Car Insurance', icon: Car },
    { id: 'two-wheeler', name: 'Two Wheeler Insurance', icon: Bike }
  ];

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNOPQRSTUVWXYZabcdefghjkmnopqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setCaptchaVerified(false);
    setCaptchaInput('');
  };

  const verifyCaptcha = () => {
    if (captchaInput === captchaText) {
      setCaptchaVerified(true);
      toast.success('CAPTCHA verified successfully');
    } else {
      toast.error('Invalid CAPTCHA. Please try again.');
      generateCaptcha();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaVerified) {
      toast.error('Please verify CAPTCHA first');
      return;
    }

    if (!consent) {
      toast.error('Please give consent to receive information');
      return;
    }

    if (!termsAgree) {
      toast.error('Please agree to terms and conditions');
      return;
    }

    setSubmitting(true);
    try {
      const leadData = {
        category: activeCategory,
        ...formData,
        country_code: '+91',
        captcha_verified: true,
        consent_given: true
      };

      await api.post('/lead-enquiries', leadData);
      toast.success('Lead submitted successfully!');
      navigate('/lead-submitted');
    } catch (error) {
      console.error('Failed to submit lead:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #fed7aa 100%)' }} data-testid="lead-enquiry-page">
      <div className="bg-gradient-to-r from-red-500 via-pink-500 to-orange-400 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4">
            Insurance Shield For Your Family
          </h1>
          <p className="text-lg opacity-90">Your family's security is our priority</p>
        </div>
      </div>

      <div className="bg-white shadow-md sticky top-16 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex overflow-x-auto gap-4 py-6 scrollbar-hide">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.name;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`flex flex-col items-center min-w-[160px] p-4 rounded-xl transition-all duration-200 ${
                    isActive ? 'bg-red-50 border-2 border-red-500' : 'bg-secondary-50 border-2 border-transparent hover:border-secondary-300'
                  }`}
                  data-testid={`category-tab-${cat.id}`}
                >
                  <div className={`w-16 h-16 rounded-full ${isActive ? 'bg-red-100' : 'bg-white'} flex items-center justify-center mb-3`}>
                    <Icon className={`h-8 w-8 ${isActive ? 'text-red-600' : 'text-secondary-600'}`} />
                  </div>
                  <span className={`text-sm font-semibold text-center ${isActive ? 'text-red-600' : 'text-secondary-700'}`}>
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl py-12">
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50 border-b">
            <CardTitle className="text-2xl font-bold text-secondary-900">
              Give your family the gift of security with <span className="text-red-600">New {activeCategory} Insurance</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input value={formData.full_name} onChange={(e) => handleInputChange('full_name', e.target.value)} placeholder="Enter Your Name" required data-testid="full-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Gender <span className="text-red-500">*</span></Label>
                  <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)} required>
                    <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mobile Number <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2">
                    <Input value="+91" disabled className="w-20 text-center" />
                    <Input type="tel" value={formData.mobile_number} onChange={(e) => handleInputChange('mobile_number', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Enter Mobile Number" required data-testid="mobile-input" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Email ID <span className="text-red-500">*</span></Label>
                  <Input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="Enter Your Email ID" required data-testid="email-input" />
                </div>
                <div className="space-y-2">
                  <Label>DOB <span className="text-red-500">*</span></Label>
                  <Input type="date" value={formData.dob} onChange={(e) => handleInputChange('dob', e.target.value)} required data-testid="dob-input" />
                </div>
                <div className="space-y-2">
                  <Label>ID Type <span className="text-red-500">*</span></Label>
                  <Select value={formData.id_type} onValueChange={(value) => handleInputChange('id_type', value)} required>
                    <SelectTrigger data-testid="id-type-select"><SelectValue placeholder="ID Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAN">PAN</SelectItem>
                      <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                      <SelectItem value="Passport">Passport</SelectItem>
                      <SelectItem value="Driving License">Driving License</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>ID Number <span className="text-red-500">*</span></Label>
                  <Input value={formData.id_number} onChange={(e) => handleInputChange('id_number', e.target.value)} placeholder="ID number" required data-testid="id-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Who do you want to insure? <span className="text-red-500">*</span></Label>
                  <Select value={formData.insured_person} onValueChange={(value) => handleInputChange('insured_person', value)} required>
                    <SelectTrigger data-testid="insured-select"><SelectValue placeholder="Who do you want to insure" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Self">Self</SelectItem>
                      <SelectItem value="Spouse">Spouse</SelectItem>
                      <SelectItem value="Children">Children</SelectItem>
                      <SelectItem value="Parents">Parents</SelectItem>
                      <SelectItem value="Family">Family</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-200">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-3">
                    <div className="bg-white p-4 rounded border-2 border-dashed border-secondary-300 text-center font-mono text-2xl tracking-widest text-secondary-900" style={{ fontFamily: 'cursive' }}>
                      {captchaText}
                    </div>
                  </div>
                  <div className="md:col-span-1 flex justify-center">
                    <Button type="button" variant="ghost" size="icon" onClick={generateCaptcha} data-testid="refresh-captcha"><RefreshCw className="h-5 w-5" /></Button>
                  </div>
                  <div className="md:col-span-5 space-y-2">
                    <Label>Enter CAPTCHA</Label>
                    <Input value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} placeholder="Enter CAPTCHA" disabled={captchaVerified} data-testid="captcha-input" />
                  </div>
                  <div className="md:col-span-3">
                    <Button type="button" onClick={verifyCaptcha} disabled={captchaVerified} className={captchaVerified ? '' : 'bg-pink-500 hover:bg-pink-600'} data-testid="verify-captcha-button">{captchaVerified ? 'Verified ✓' : 'Verify'}</Button>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="consent-checkbox" />
                <Label htmlFor="consent" className="text-sm text-secondary-700 cursor-pointer">I agree Noble Shield can contact me for any insurance update.</Label>
              </div>

              <Button type="submit" size="lg" className="w-full bg-pink-500 hover:bg-pink-600 text-white font-semibold" disabled={submitting} data-testid="submit-lead-button">
                {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Submitting...</> : 'Get Quote'}
              </Button>

              <div className="text-center"><p className="text-red-600 font-bold text-lg">Go for protection that gives you complete peace of mind.</p></div>

              <div className="flex items-start gap-3 border-t pt-6">
                <Checkbox id="terms" checked={termsAgree} onCheckedChange={setTermsAgree} data-testid="terms-checkbox" />
                <Label htmlFor="terms" className="text-sm text-secondary-700 cursor-pointer">I Agree to <a href="/page/terms" className="text-primary-600 hover:underline" target="_blank">terms and conditions</a></Label>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadEnquiryPage;
